import React, { useState, Children } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
export const PricingView = () => {
  // Sample room types and their base prices
  const roomTypes = [{
    id: 1,
    name: 'Standard Room',
    basePrice: 129,
    currentPrice: 178,
    lastUpdated: '2023-09-22 14:35',
    capacity: '2 Adults',
    beds: '1 Queen'
  }, {
    id: 2,
    name: 'Deluxe Room',
    basePrice: 179,
    currentPrice: 253,
    lastUpdated: '2023-09-22 14:35',
    capacity: '2 Adults, 1 Child',
    beds: '1 King'
  }, {
    id: 3,
    name: 'Ocean View Suite',
    basePrice: 249,
    currentPrice: 312,
    lastUpdated: '2023-09-22 14:35',
    capacity: '2 Adults, 2 Children',
    beds: '1 King, 1 Sofa Bed'
  }, {
    id: 4,
    name: 'Executive Suite',
    basePrice: 329,
    currentPrice: 395,
    lastUpdated: '2023-09-22 14:35',
    capacity: '3 Adults',
    beds: '1 King, 1 Queen'
  }];
  // Factors affecting dynamic pricing
  const [demandFactor, setDemandFactor] = useState(1.15); // High demand season
  const [dayOfWeekFactor, setDayOfWeekFactor] = useState(1.25); // Weekend pricing
  const [occupancyRate, setOccupancyRate] = useState(78); // Current hotel occupancy
  // Generate price history data for the past 24 hours
  const generatePriceHistoryData = (basePrice, roomId) => {
    const now = new Date();
    const data = [];
    // Create variations based on the room ID to make each chart different
    const variationFactor = roomId * 0.05;
    // Generate data for each hour in the last 24 hours
    for (let i = 24; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      const hourOfDay = time.getHours();
      // Factors that affect price
      const timeOfDayFactor = hourOfDay >= 17 && hourOfDay <= 22 ? 1.1 : 1.0; // Higher prices in evening
      const randomFactor = 0.95 + Math.random() * 0.1; // Random variation
      const hourSpecificFactor = 1 + Math.sin(hourOfDay / 24 * Math.PI * 2) * 0.05 * variationFactor;
      // Calculate price for this hour
      const price = Math.round(basePrice * demandFactor * (i < 8 ? dayOfWeekFactor : 1.15) * (
      // Recent hours use current day factor
      0.5 + (occupancyRate - (i > 12 ? 5 : 0)) / 100) *
      // Occupancy changes
      timeOfDayFactor * randomFactor * hourSpecificFactor);
      // Format time string
      const timeString = time.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
      const dateString = time.toLocaleDateString([], {
        month: 'short',
        day: 'numeric'
      });
      data.push({
        time: `${dateString}, ${timeString}`,
        price: price,
        hour: time.getHours(),
        timestamp: time.getTime()
      });
    }
    return data;
  };
  // Generate price history for each room type
  const roomPriceHistory = roomTypes.map(room => ({
    ...room,
    priceHistory: generatePriceHistoryData(room.basePrice, room.id)
  }));
  // Get the current time for display
  const currentTime = new Date().toLocaleString([], {
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  // Custom tooltip for the chart
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      return <div className="bg-white p-3 border border-gray-200 rounded-md shadow-sm" data-id="element-257">
          <p className="text-sm font-medium" data-id="element-258">{label}</p>
          <p className="text-sm text-teal-600 font-semibold" data-id="element-259">
            ${payload[0].value}
          </p>
        </div>;
    }
    return null;
  };
  return <div data-id="element-260">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6" data-id="element-261">
        Dynamic Room Pricing
      </h2>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-6" data-id="element-262">
        <div className="flex flex-wrap gap-4 items-center" data-id="element-263">
          <div className="flex items-center" data-id="element-264">
            <span className="text-gray-700 font-medium mr-2" data-id="element-265">
              Current Occupancy:
            </span>
            <span className="text-teal-600 font-bold" data-id="element-266">{occupancyRate}%</span>
          </div>
          <div className="flex items-center" data-id="element-267">
            <span className="text-gray-700 font-medium mr-2" data-id="element-268">
              Demand Factor:
            </span>
            <span className="text-teal-600 font-bold" data-id="element-269">
              {(demandFactor * 100 - 100).toFixed(0)}% above base
            </span>
          </div>
          <div className="flex items-center" data-id="element-270">
            <span className="text-gray-700 font-medium mr-2" data-id="element-271">Day Pricing:</span>
            <span className="text-teal-600 font-bold" data-id="element-272">Weekend Rates</span>
          </div>
          <div className="ml-auto flex items-center" data-id="element-273">
            <span className="text-xs text-gray-500 mr-2" data-id="element-274">
              Data source: XYZ Pricing Service
            </span>
            <span className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded-full" data-id="element-275">
              Last updated: {currentTime}
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 mb-6" data-id="element-276">
        {roomPriceHistory.map(room => {
        // Get first and last price to calculate change
        const firstPrice = room.priceHistory[0].price;
        const lastPrice = room.priceHistory[room.priceHistory.length - 1].price;
        const priceChange = lastPrice - firstPrice;
        const percentChange = (priceChange / firstPrice * 100).toFixed(1);
        // Determine color based on price change
        const changeColor = priceChange >= 0 ? 'text-green-600' : 'text-red-600';
        const changeArrow = priceChange >= 0 ? '↑' : '↓';
        return <div key={room.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-277">
              <div className="p-4 border-b border-gray-200" data-id="element-278">
                <div className="flex justify-between items-start mb-2" data-id="element-279">
                  <div data-id="element-280">
                    <h3 className="text-lg font-medium text-gray-800" data-id="element-281">
                      {room.name}
                    </h3>
                    <div className="flex gap-3 mt-2 text-xs text-gray-500" data-id="element-282">
                      <span className="flex items-center" data-id="element-283">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-284">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" data-id="element-285" />
                        </svg>
                        {room.capacity}
                      </span>
                      <span className="flex items-center" data-id="element-286">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-287">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" data-id="element-288" />
                        </svg>
                        {room.beds}
                      </span>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-289">
                    <div className="flex flex-col items-end" data-id="element-290">
                      <span className="text-gray-500 text-sm" data-id="element-291">
                        Current Price:
                      </span>
                      <div className="flex items-baseline" data-id="element-292">
                        <span className="text-gray-400 text-sm line-through mr-2" data-id="element-293">
                          ${room.basePrice}
                        </span>
                        <span className="text-2xl font-bold text-teal-600" data-id="element-294">
                          ${lastPrice}
                        </span>
                      </div>
                      <div className={`flex items-center text-sm mt-1 ${changeColor}`} data-id="element-295">
                        <span className="font-medium" data-id="element-296">
                          {changeArrow} {Math.abs(percentChange)}%
                        </span>
                        <span className="ml-1 text-xs text-gray-500" data-id="element-297">
                          past 24h
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4" data-id="element-298">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-299">
                  Price Trend (Last 24 Hours)
                </h4>
                <div className="h-64" data-id="element-300">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-301">
                    <LineChart data={room.priceHistory} margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }} data-id="element-302">
                      <CartesianGrid strokeDasharray="3 3" data-id="element-303" />
                      <XAxis dataKey="time" tick={{
                    fontSize: 12
                  }} interval="preserveStartEnd" minTickGap={50} data-id="element-304" />
                      <YAxis domain={['auto', 'auto']} tick={{
                    fontSize: 12
                  }} tickFormatter={value => `$${value}`} data-id="element-305" />
                      <Tooltip content={<CustomTooltip data-id="element-307" />} data-id="element-306" />
                      <Legend data-id="element-308" />
                      <ReferenceLine y={room.basePrice} stroke="#666" strokeDasharray="3 3" label={{
                    value: 'Base Price',
                    position: 'insideBottomRight',
                    fontSize: 12
                  }} data-id="element-309" />
                      <Line type="monotone" dataKey="price" name="Room Price" stroke="#0088FE" strokeWidth={2} dot={false} activeDot={{
                    r: 6
                  }} data-id="element-310" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200" data-id="element-311">
                <div className="flex justify-between items-center" data-id="element-312">
                  <div className="text-sm text-gray-500" data-id="element-313">
                    Base price: ${room.basePrice}
                  </div>
                  <div className="text-sm" data-id="element-314">
                    <span className="font-medium" data-id="element-315">Peak time:</span>
                    <span className="ml-1 text-teal-600" data-id="element-316">
                      {room.priceHistory.reduce((max, point) => point.price > max.price ? point : max, {
                    price: 0
                  }).time}
                    </span>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
};